import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Edit2, Trash2, Save, X, Tag, Percent, DollarSign,
    Calendar, Users, ToggleLeft, ToggleRight, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../lib/supabase';

const AdminPromoCodes = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { language } = useLanguage();
    const { showModal } = useNotification();
    const [promoCodes, setPromoCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [showNewForm, setShowNewForm] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        description: '',
        max_uses: '',
        valid_until: ''
    });

    useEffect(() => {
        checkAdminRole();
        loadPromoCodes();
    }, [user]);

    const checkAdminRole = async () => {
        if (!user) {
            navigate('/auth/login');
            return;
        }

        const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();

        if (!profile || profile.role !== 'admin') {
            showModal(
                language === 'tr' ? 'Yetkisiz Erişim' : 'Unauthorized',
                language === 'tr' ? 'Bu sayfaya erişim yetkiniz yok.' : 'You do not have permission to access this page.',
                'error'
            );
            navigate('/');
        }
    };

    const loadPromoCodes = async () => {
        try {
            const { data, error } = await supabase
                .from('promo_codes')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setPromoCodes(data || []);
        } catch (error) {
            console.error('Error loading promo codes:', error);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Promosyon kodları yüklenemedi.' : 'Failed to load promo codes.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            const { error } = await supabase.from('promo_codes').insert({
                code: formData.code.toUpperCase(),
                discount_type: formData.discount_type,
                discount_value: parseFloat(formData.discount_value),
                description: formData.description,
                max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
                valid_until: formData.valid_until || null,
                created_by: user.id
            });

            if (error) throw error;

            showModal(
                language === 'tr' ? 'Başarılı' : 'Success',
                language === 'tr' ? 'Promosyon kodu oluşturuldu.' : 'Promo code created successfully.',
                'success'
            );

            setShowNewForm(false);
            setFormData({
                code: '',
                discount_type: 'percentage',
                discount_value: '',
                description: '',
                max_uses: '',
                valid_until: ''
            });
            loadPromoCodes();
        } catch (error) {
            console.error('Error creating promo code:', error);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                error.message || (language === 'tr' ? 'Promosyon kodu oluşturulamadı.' : 'Failed to create promo code.'),
                'error'
            );
        }
    };

    const handleToggleActive = async (id, currentStatus) => {
        try {
            const { error } = await supabase
                .from('promo_codes')
                .update({ is_active: !currentStatus })
                .eq('id', id);

            if (error) throw error;

            showModal(
                language === 'tr' ? 'Başarılı' : 'Success',
                language === 'tr' ? 'Durum güncellendi.' : 'Status updated.',
                'success'
            );
            loadPromoCodes();
        } catch (error) {
            console.error('Error toggling status:', error);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Durum güncellenemedi.' : 'Failed to update status.',
                'error'
            );
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(language === 'tr' ? 'Bu promosyon kodunu silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this promo code?')) {
            return;
        }

        try {
            const { error } = await supabase
                .from('promo_codes')
                .delete()
                .eq('id', id);

            if (error) throw error;

            showModal(
                language === 'tr' ? 'Başarılı' : 'Success',
                language === 'tr' ? 'Promosyon kodu silindi.' : 'Promo code deleted.',
                'success'
            );
            loadPromoCodes();
        } catch (error) {
            console.error('Error deleting promo code:', error);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Promosyon kodu silinemedi.' : 'Failed to delete promo code.',
                'error'
            );
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <Tag className="w-8 h-8 text-blue-600" />
                            {language === 'tr' ? 'Promosyon Kodları' : 'Promo Codes'}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            {language === 'tr' ? 'İndirim kodlarını yönetin' : 'Manage discount codes'}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowNewForm(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        {language === 'tr' ? 'Yeni Kod' : 'New Code'}
                    </button>
                </div>

                {/* New Promo Code Form */}
                <AnimatePresence>
                    {showNewForm && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 mb-6"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                    {language === 'tr' ? 'Yeni Promosyon Kodu' : 'New Promo Code'}
                                </h3>
                                <button
                                    onClick={() => setShowNewForm(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'Kod' : 'Code'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                        placeholder="WELCOME10"
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'İndirim Tipi' : 'Discount Type'}
                                    </label>
                                    <select
                                        value={formData.discount_type}
                                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    >
                                        <option value="percentage">{language === 'tr' ? 'Yüzde (%)' : 'Percentage (%)'}</option>
                                        <option value="fixed">{language === 'tr' ? 'Sabit ($)' : 'Fixed ($)'}</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'İndirim Değeri' : 'Discount Value'}
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.discount_value}
                                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                                        placeholder={formData.discount_type === 'percentage' ? '10' : '50'}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'Maksimum Kullanım' : 'Max Uses'} ({language === 'tr' ? 'Opsiyonel' : 'Optional'})
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.max_uses}
                                        onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                                        placeholder={language === 'tr' ? 'Sınırsız' : 'Unlimited'}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'Açıklama' : 'Description'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder={language === 'tr' ? 'Hoş geldin indirimi - %10 indirim' : 'Welcome discount - 10% off'}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'tr' ? 'Geçerlilik Tarihi' : 'Valid Until'} ({language === 'tr' ? 'Opsiyonel' : 'Optional'})
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.valid_until}
                                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleCreate}
                                disabled={!formData.code || !formData.discount_value}
                                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {language === 'tr' ? 'Oluştur' : 'Create'}
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Promo Codes List */}
                <div className="space-y-4">
                    {promoCodes.map((promo) => (
                        <motion.div
                            key={promo.id}
                            layout
                            className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                            {promo.code}
                                        </h3>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${promo.is_active
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                            }`}>
                                            {promo.is_active ? (language === 'tr' ? 'Aktif' : 'Active') : (language === 'tr' ? 'Pasif' : 'Inactive')}
                                        </span>
                                    </div>

                                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                                        {promo.description}
                                    </p>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="flex items-center gap-2 text-sm">
                                            {promo.discount_type === 'percentage' ? (
                                                <Percent className="w-4 h-4 text-blue-600" />
                                            ) : (
                                                <DollarSign className="w-4 h-4 text-green-600" />
                                            )}
                                            <span className="text-gray-700 dark:text-gray-300">
                                                {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm">
                                            <Users className="w-4 h-4 text-purple-600" />
                                            <span className="text-gray-700 dark:text-gray-300">
                                                {promo.current_uses} / {promo.max_uses || '∞'}
                                            </span>
                                        </div>

                                        {promo.valid_until && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <Calendar className="w-4 h-4 text-orange-600" />
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {new Date(promo.valid_until).toLocaleDateString()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 ml-4">
                                    <button
                                        onClick={() => handleToggleActive(promo.id, promo.is_active)}
                                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                        title={promo.is_active ? (language === 'tr' ? 'Pasifleştir' : 'Deactivate') : (language === 'tr' ? 'Aktifleştir' : 'Activate')}
                                    >
                                        {promo.is_active ? (
                                            <ToggleRight className="w-6 h-6" />
                                        ) : (
                                            <ToggleLeft className="w-6 h-6" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(promo.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                        title={language === 'tr' ? 'Sil' : 'Delete'}
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}

                    {promoCodes.length === 0 && (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            {language === 'tr' ? 'Henüz promosyon kodu yok' : 'No promo codes yet'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPromoCodes;
